/**
 * Zustand store для карточки номера.
 */
import { create } from "zustand"
import { vehiclesApi, signalsApi } from "../api/endpoints"
import type { VehicleCard, SignalType } from "../types/api"

interface VehicleState {
  currentVehicle: VehicleCard | null
  signalTypes: SignalType[]
  isLoading: boolean
  isSearching: boolean
  error: string | null

  searchVehicle: (plate: string) => Promise<VehicleCard>
  loadSuggestedVehicle: () => Promise<VehicleCard>
  loadSignalTypes: () => Promise<void>
  refreshVehicle: (vehicleId: number) => Promise<void>
  shareVehicle: (vehicleId: number) => Promise<string>
  setCurrentVehicle: (vehicle: VehicleCard | null) => void
}

let suggestedVehicleRequest: Promise<VehicleCard> | null = null
let vehicleSelectionRevision = 0
let vehicleIntentRevision = 0
let latestSearchRequestRevision = 0

export const useVehicleStore = create<VehicleState>((set, get) => ({
  currentVehicle: null,
  signalTypes: [],
  isLoading: false,
  isSearching: false,
  error: null,

  loadSuggestedVehicle: () => {
    if (suggestedVehicleRequest) return suggestedVehicleRequest
    const startingSelectionRevision = vehicleSelectionRevision
    const startingIntentRevision = vehicleIntentRevision
    suggestedVehicleRequest = (async () => {
      set({ isLoading: true, error: null })
      try {
        const vehicle = await vehiclesApi.getSuggestedCard()
        if (vehicleSelectionRevision === startingSelectionRevision && !get().currentVehicle) {
          set({ currentVehicle: vehicle })
        }
        return vehicle
      } catch (err: any) {
        if (vehicleSelectionRevision === startingSelectionRevision && vehicleIntentRevision === startingIntentRevision) {
          set({ error: err.message || "Ошибка автоподбора карточки" })
        }
        throw err
      } finally {
        suggestedVehicleRequest = null
        set({ isLoading: false })
      }
    })()
    return suggestedVehicleRequest
  },

  searchVehicle: async (plate: string) => {
    const intentRevision = ++vehicleIntentRevision
    const searchRequestRevision = ++latestSearchRequestRevision
    set({ isSearching: true, error: null })
    try {
      const response = await vehiclesApi.search(plate)
      if (intentRevision === vehicleIntentRevision) {
        vehicleSelectionRevision += 1
        set({ currentVehicle: response.vehicle, error: null })
      }
      return response.vehicle
    } catch (err: any) {
      if (intentRevision === vehicleIntentRevision && searchRequestRevision === latestSearchRequestRevision) {
        set({ error: err.message || "Ошибка поиска" })
      }
      throw err
    } finally {
      if (searchRequestRevision === latestSearchRequestRevision) {
        set({ isSearching: false })
      }
    }
  },

  loadSignalTypes: async () => {
    if (get().signalTypes.length > 0) return
    try {
      const types = await signalsApi.getTypes()
      set({ signalTypes: types })
    } catch {
      // Игнорировать
    }
  },

  refreshVehicle: async (vehicleId: number) => {
    const intentRevision = ++vehicleIntentRevision
    const vehicle = await vehiclesApi.getCard(vehicleId)
    if (intentRevision === vehicleIntentRevision) {
      vehicleSelectionRevision += 1
      set({ currentVehicle: vehicle, error: null })
    }
  },

  shareVehicle: async (vehicleId: number) => {
    const result = await vehiclesApi.share(vehicleId)
    return result.deep_link
  },

  setCurrentVehicle: (vehicle) => {
    vehicleIntentRevision += 1
    vehicleSelectionRevision += 1
    set({ currentVehicle: vehicle })
  },
}))
